import { ArrowGo, EmoEye, EmoNeutral, Filter, Zoom } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { Plus, Users } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import FeedItem, {
  type FeedItem as FeedItemType,
} from "@/components/feed/feed-item"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import { forumPath, forumPostsNewPath } from "@/routes"

interface ForumIndexProps {
  recent_posts: FeedItemType[]
  popular_posts: FeedItemType[]
  categories: {
    id: number
    name: string
    description?: string
    icon?: string
    color?: string
    post_count: number
  }[]
  filters?: {
    category?: string
    search?: string
    sort?: string
  }
  stats: {
    total_posts: number
    total_answers: number
    total_users: number
    resolved_posts: number
  }
  current_city: unknown
  pagination: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

export default function ForumIndex({
  recent_posts = [],
  categories = [],
  filters = {},
  pagination,
}: ForumIndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search ?? "")
  const [previousSearchTerm, setPreviousSearchTerm] = useState<string>("")
  const [showFilters, setShowFilters] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedSort, setSelectedSort] = useState("recent")
  const [allPosts, setAllPosts] = useState(recent_posts)
  const [isLoading, setIsLoading] = useState(false)

  // Sync local state with props
  useEffect(() => {
    if (recent_posts.length > 0) {
      setAllPosts((prevPosts) => {
        const urlParams = new URLSearchParams(window.location.search)
        const currentOffset = parseInt(urlParams.get("offset") ?? "0")
        const isLoadMore = currentOffset > 0

        if (!isLoadMore) {
          return recent_posts
        } else {
          const newPosts = recent_posts.filter(
            (newPost) =>
              !prevPosts.some((existingPost) => existingPost.id === newPost.id),
          )
          return [...prevPosts, ...newPosts]
        }
      })
      setIsLoading(false)
    } else if (recent_posts.length === 0 && !isLoading) {
      setAllPosts([])
    }
  }, [recent_posts, isLoading])

  useEffect(() => {
    setSearchTerm(filters.search ?? "")

    const categoryValue = filters.category?.toString() ?? "all"
    if (categoryValue !== selectedCategory) {
      setSelectedCategory(categoryValue)
    }

    const sortValue = filters.sort ?? "recent"
    if (sortValue !== selectedSort) {
      setSelectedSort(sortValue)
    }
  }, [
    filters.search,
    filters.category,
    filters.sort,
    selectedCategory,
    selectedSort,
  ])

  useEffect(() => {
    if (
      previousSearchTerm.trim() &&
      !searchTerm.trim() &&
      previousSearchTerm !== searchTerm
    ) {
      handleSearch()
    }
    setPreviousSearchTerm(searchTerm)
  }, [searchTerm, previousSearchTerm])

  const handleSearch = (event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault()
    }
    const params: Record<string, string> = {}

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "" && value !== "all") {
        params[key] = value
      }
    })

    if (searchTerm?.trim()) {
      params.search = searchTerm.trim()
    } else if (params.search) {
      delete params.search
    }

    if (!params.category) {
      params.category = "all"
    }

    router.get(forumPath(), params, {
      preserveState: true,
      preserveScroll: true,
      only: ["recent_posts", "popular_posts", "filters", "pagination"],
    })
  }

  const handleFilterChange = (key: string, value: string) => {
    const params: Record<string, string> = {}

    Object.entries(filters).forEach(([filterKey, filterValue]) => {
      if (filterValue && filterValue !== "" && filterValue !== "all") {
        params[filterKey] = filterValue
      }
    })

    if (key === "category") {
      if (value === "all" || !value) {
        params[key] = "all"
      } else {
        params[key] = value
      }
    } else {
      if (value && value !== "all") {
        params[key] = value
      }
    }

    router.get(forumPath(), params, {
      preserveState: true,
      preserveScroll: true,
      only: ["recent_posts", "popular_posts", "filters", "pagination"],
    })
  }

  const handleLoadMore = () => {
    if (isLoading || !pagination.has_more) return

    setIsLoading(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      forumPath(),
      {
        ...filters,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["recent_posts", "pagination"],
        onFinish: () => setIsLoading(false),
      },
    )
  }

  return (
    <AppLayout>
      <Head title="Foro" />

      <div className="mx-auto max-w-6xl space-y-[15px] md:p-6">
        <div className="flex flex-col">
          <div className="flex items-center justify-center gap-4">
            <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
              <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                <img
                  src={EmoEye}
                  alt="Eye"
                  className="h-full w-full object-contain"
                />
              </span>
              Foro Comunitario
            </h1>
            <AuthButton
              variant="default"
              className="flex flex-shrink-0 items-center gap-2"
              onClick={() => router.visit(forumPostsNewPath())}
              authAction="register"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nueva consulta</span>
            </AuthButton>
          </div>
          <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
            Conecta, pregunta y comparte con la comunidad argentina
          </p>
        </div>

        {/* Stats Cards */}
        {/* <div className="grid grid-cols-3  gap-4">
          <Card>
            <CardContent className="p-0 text-center">
              <div className="text-2xl font-bold text-primary">{stats.total_posts}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0 text-center">
              <div className="text-2xl font-bold text-primary">{stats.resolved_posts}</div>
              <div className="text-sm text-muted-foreground">Resueltas</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0 text-center">
              <div className="text-2xl font-bold text-primary">{stats.total_users}</div>
              <div className="text-sm text-muted-foreground">Miembros activos</div>
            </CardContent>
          </Card>
        </div> */}

        <Tabs defaultValue="posts" className="space-y-6">
          <div className="flex justify-center">
            <TabsList className="bg-foreground inline-flex h-auto rounded-[15px] px-[10px] py-[10px] tracking-[0.26px]">
              <TabsTrigger
                value="posts"
                className="data-[state=active]:bg-card flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
              >
                Consultas
              </TabsTrigger>
              <TabsTrigger
                value="categories"
                className="data-[state=active]:bg-card flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
              >
                Categorías
              </TabsTrigger>
            </TabsList>
          </div>
          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-6">
            {/* Search and Filters Section */}
            <div className="mx-[12px]">
              <div className="flex w-full items-center gap-[8px]">
                <form
                  onSubmit={handleSearch}
                  className="bg-card w-full rounded-[12px]"
                >
                  <div className="relative flex items-end">
                    <Zoom className="text-primary bg-card mx-[10px] my-[6px] h-[34px] fill-current" />
                    <Input
                      placeholder="Buscar productos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="placeholder:text-muted-foreground w-full border-0 px-0 pt-0 pb-[6px] text-[15px] font-light tracking-[0.07px] opacity-[0.75] shadow-[0_0_0_0px_rgba(0,0,0,0)] focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      className="text-primary bg-card m-[6px] h-[34px] w-[34px] rounded-[8px] shadow-[0px_0px_4px_#00000029]"
                    >
                      <ArrowGo className="text-primary size-6 fill-current" />
                    </Button>
                  </div>
                </form>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`bg-card h-[34px] w-[34px] rounded-[8px] border-0 shadow-[0px_0px_4px_#00000029] ${
                    showFilters && "h-[34px] w-[34px] shadow-md"
                  }`}
                >
                  <Filter className="text-primary size-6 fill-current" />
                </Button>
              </div>

              {/* Filters Section */}
              {showFilters && (
                <Card className="relative mt-2 border-0 px-3 py-[15px] shadow-none">
                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="text-primary absolute top-3 right-3 flex h-[17px] w-[17px] items-center justify-center rounded-full bg-transparent text-[17px] leading-none"
                  >
                    ×
                  </button>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    {/* Category Filter */}
                    <div className="space-y-3">
                      <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                        Categoría
                      </label>
                      <Select
                        value={selectedCategory}
                        onValueChange={(value) => {
                          setSelectedCategory(value)
                          handleFilterChange("category", value)
                        }}
                      >
                        <SelectTrigger className="bg-popover focus:border-primary h-[40px] w-full rounded-[9px] transition-colors">
                          <SelectValue>
                            {selectedCategory === "all"
                              ? "📦 Todas"
                              : (categories.find(
                                  (c) => c.id.toString() === selectedCategory,
                                )?.name ?? "📦 Todas")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value="all"
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            📦 Todas
                          </SelectItem>
                          {categories.map((category) => (
                            <SelectItem
                              key={category.id}
                              value={category.id.toString()}
                              className="focus:bg-background focus:text-primary font-medium"
                            >
                              {category.icon ?? "🛍️"} {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sort Filter */}
                    <div className="space-y-3">
                      <label className="text-foreground text-[12px] font-semibold tracking-[0.24px]">
                        Ordenado por
                      </label>
                      <Select
                        value={selectedSort}
                        onValueChange={(value) => {
                          setSelectedSort(value)
                          handleFilterChange("sort", value)
                        }}
                      >
                        <SelectTrigger className="bg-popover focus:border-primary h-[40px] w-full rounded-[9px] transition-colors">
                          <SelectValue placeholder="Seleccionar orden" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value="recent"
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            📅 Más recientes
                          </SelectItem>
                          <SelectItem
                            value="popular"
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            👥 Más populares
                          </SelectItem>
                          <SelectItem
                            value="answers"
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            💬 Más comentados
                          </SelectItem>
                          <SelectItem
                            value="pinned"
                            className="focus:bg-background focus:text-primary font-medium"
                          >
                            📌 Fijados
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Posts List */}
            <div className="">
              {allPosts.length > 0 ? (
                allPosts.map((post) => (
                  <FeedItem key={post.id} item={post} variant="card" />
                ))
              ) : (
                <div
                  className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                  style={{ background: "var(--gradient-highlight)" }}
                >
                  <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                    <img src={EmoNeutral} alt="Sin contenido" />
                  </div>

                  <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                    No se encontraron consultas
                  </h3>
                </div>
              )}

              {/* Load More Button */}
              {allPosts.length > 0 && pagination.has_more && (
                <div className="text-center">
                  <Button
                    variant="ghost"
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    className="text-primary hover:bg-muted"
                  >
                    {isLoading ? "Cargando..." : "Ver más consultas"}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-3 px-[12px]">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  href={`${forumPath()}?category=${category.id}`}
                >
                  <Card className="h-full cursor-pointer transition-all duration-200 hover:shadow-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">
                            {category.icon ?? "💬"}
                          </div>
                          <div>
                            <CardTitle className="text-lg">
                              {category.name}
                            </CardTitle>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {category.post_count} consultas
                        </Badge>
                      </div>
                    </CardHeader>
                    {category.description && (
                      <CardContent className="pt-0">
                        <p className="text-muted-foreground line-clamp-2 text-sm">
                          {category.description}
                        </p>
                      </CardContent>
                    )}
                  </Card>
                </Link>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Community Guidelines */}
        <div className="">
          <CardContent className="p-6">
            <h3 className="text-foreground mb-3 flex items-center gap-2 text-lg font-semibold">
              <Users className="h-5 w-5" />
              Normas de la Comunidad
            </h3>
            <div className="text-muted-foreground grid gap-3 text-sm md:grid-cols-2">
              <div className="flex items-start gap-2">
                <span>🤝</span>
                <span>Mantén un tono respetuoso y constructivo</span>
              </div>
              <div className="flex items-start gap-2">
                <span>🎯</span>
                <span>Sé específico en tus consultas</span>
              </div>
              <div className="flex items-start gap-2">
                <span>🔍</span>
                <span>Busca antes de crear una nueva consulta</span>
              </div>
              <div className="flex items-start gap-2">
                <span>✅</span>
                <span>Marca como resuelto cuando recibas ayuda</span>
              </div>
            </div>
          </CardContent>
        </div>
      </div>
    </AppLayout>
  )
}
